import React, { useState } from 'react';
import { useTranslation } from '@/i18n';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/utils/apiClient';
import { ToolAnalyticsPanel } from './ToolAnalyticsPanel';
import { UsageTrendsWidget } from './UsageTrendsWidget';
import { PerformanceMetrics } from './PerformanceMetrics';
import { ScheduledQuerySummary } from './ScheduledQuerySummary';
import { RecipeHitRatePanel } from './RecipeHitRatePanel';
import { ProviderVolumeChart } from './ProviderVolumeChart';
import { ToolFrequencyPanel } from './ToolFrequencyPanel';
import { CompoundStatsPanel } from './CompoundStatsPanel';

export const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const at = (t as any).analytics;
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  // STORY-188: data is scoped to the current user; admins may flip to the
  // global view. The key on the grid remounts every panel so each refetches
  // with the new scope.
  const [allUsers, setAllUsers] = useState(false);
  // Set during render (not in an effect): child panels fetch in their own
  // mount effects, which run BEFORE parent effects — the flag must already
  // be correct when they fire.
  apiClient.setAnalyticsScope(allUsers);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {at?.title ?? 'Analytics'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {at?.usageTrends ?? 'Usage Trends'} & {at?.performance ?? 'Performance'}
          </p>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allUsers}
              onChange={(e) => setAllUsers(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            {at?.allUsers ?? 'All users'}
          </label>
        )}
      </div>

      {/* Two-column grid for larger screens, single column on mobile */}
      <div key={allUsers ? 'all' : 'own'} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UsageTrendsWidget />
        <ToolAnalyticsPanel />
        <PerformanceMetrics />
        <ScheduledQuerySummary />
        <RecipeHitRatePanel />
        <ProviderVolumeChart />
        <ToolFrequencyPanel />
        {/* STORY-301: global, admin-only data — a normal grid item, so it
            starts on the same row line as its neighbour. */}
        {isAdmin && <CompoundStatsPanel />}
      </div>
    </div>
  );
};
